import { fetchAuthSession } from "aws-amplify/auth";
import type {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import axios from "axios";

import { HttpError } from "@/types/error";
import * as Sentry from "@sentry/browser";

import { transformEmptyValuesToNull } from "./utils";

declare module "axios" {
  export interface AxiosRequestConfig {
    _retry?: boolean;
  }
}

const axiosClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

// Request interceptor to transform empty values to null for POST and PATCH requests
const onRequest = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  // Only apply for POST and PATCH
  if (config.method !== "post" && config.method !== "patch") {
    return config;
  }

  // Only proceed if there's data to transform
  if (!config.data) {
    return config;
  }

  // Skip transformation if data is already a string (e.g., from retry)
  if (typeof config.data === "string") {
    return config;
  }

  config.data = transformEmptyValuesToNull(config.data);

  return config;
};

const onResponseError = async (
  error: AxiosError<HttpError>,
): Promise<AxiosError> => {
  const originalRequest = error.config as InternalAxiosRequestConfig & {
    _retry: boolean;
  };

  if (
    error.response?.status === 401 &&
    originalRequest &&
    !originalRequest?._retry
  ) {
    // add a _retry flag to avoid infinite loop
    originalRequest._retry = true;
    const session = await fetchAuthSession();

    const tokens = session.tokens;

    if (tokens && tokens.accessToken) {
      // set header to the failed request
      originalRequest.headers.setAuthorization(
        `Bearer ${tokens.accessToken.toString()}`,
      );

      //set header to the axiosClient instance
      axiosClient.defaults.headers.common.Authorization = `Bearer ${tokens.accessToken.toString()}`;
      return axiosClient(originalRequest);
    }
  }
  if (process.env.NODE_ENV === "development") {
    console.error(`[axios response error]:`, JSON.stringify(error));
  }
  return Promise.reject(error.response?.data);
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
  return response;
};

const onRequestError = (error: unknown) => {
  // send to Sentry
  Sentry.captureException(error);
  // re‐throw so your callers can still `.catch()`
  return Promise.reject(error);
};

axiosClient.interceptors.request.use(onRequest, onRequestError);
axiosClient.interceptors.response.use(onResponse, onResponseError);

export { axiosClient };
